import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import {
  defaultSEO,
  defaultOpenGraph,
  defaultTwitter,
  toAbsoluteUrl,
  buildCanonicalUrl,
  BASE_URL,
} from '../../seo/seoConfig';

const ensureArray = (data) => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

/**
 * Enhanced SEO Component with Dynamic Metadata
 * Optimized for Google Bot and Core Web Vitals
 */
const SEO = ({
  title,
  description,
  keywords,
  author,
  image,
  url,
  canonical,
  robots,
  type,
  locale,
  structuredData,
  openGraph,
  twitter,
  additionalMeta = [],
  prev,
  next,
  children,
  noindex = false,
  nofollow = false,
  article,
  product,
  pageReady = false,
}) => {
  const location = useLocation();
  const [fallbackReady, setFallbackReady] = useState(false);

  // react-snap safety net: if the page hasn't signalled pageReady within 12 seconds
  // (due to slow APIs or network latency), force ready to prevent build timeout.
  useEffect(() => {
    const t = setTimeout(() => {
      setFallbackReady(true);
    }, 12000);
    return () => clearTimeout(t);
  }, []);
  const canonicalUrl = useCanonicalUrl(canonical || url || location.pathname);

  // Track SPA page views for GA4 — window.gtag exists as soon as the static
  // gtag.js snippet in public/index.html's <head> loads (unconditional, not
  // gated behind cookie consent). Its one-time config call on initial load
  // doesn't see later client-side route changes, so this re-fires config with
  // the new page_path on every navigation.
  useEffect(() => {
    const gaId = process.env.REACT_APP_GA_ID;
    if (typeof window !== 'undefined' && window.gtag && gaId) {
      window.gtag('config', gaId, {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  const merged = {
    ...defaultSEO,
    title: title || defaultSEO.title,
    description: description || defaultSEO.description,
    keywords: keywords || defaultSEO.keywords,
    author: author || defaultSEO.author,
    image: image || defaultSEO.image,
    robots: noindex || nofollow
      ? `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`
      : (robots || defaultSEO.robots),
    type: type || defaultSEO.type,
    locale: locale || defaultSEO.locale,
  };

  const resolvedImage = toAbsoluteUrl(merged.image);
  const imageWidth = 1200;
  const imageHeight = 630;

  const ogConfig = {
    ...defaultOpenGraph,
    ...openGraph,
    title: openGraph?.title || merged.title,
    description: openGraph?.description || merged.description,
    url: openGraph?.url || canonicalUrl,
    image: openGraph?.image ? toAbsoluteUrl(openGraph.image) : resolvedImage,
    type: openGraph?.type || merged.type,
    image_width: openGraph?.image_width || imageWidth,
    image_height: openGraph?.image_height || imageHeight,
  };

  const twitterConfig = {
    ...defaultTwitter,
    ...twitter,
    title: twitter?.title || merged.title,
    description: twitter?.description || merged.description,
    image: twitter?.image ? toAbsoluteUrl(twitter.image) : resolvedImage,
  };

  const structuredDataEntries = ensureArray(structuredData);

  // Preconnect to the API origin, but only when it's not already covered by the
  // static storage-origin preconnect in public/index.html — on stage/production
  // the API and storage base are the same host, so a second preconnect there is
  // a wasted connection (browsers cap useful preconnects at a handful).
  // Fonts are already preconnected statically in public/index.html, so they're
  // not repeated here.
  const performanceHints = (() => {
    const apiBase = process.env.REACT_APP_API_BASE || `http://${process.env.REACT_APP_IP || 'localhost'}:3100/api`;
    const storageBase = process.env.REACT_APP_STORAGE_BASE || '';
    try {
      const apiOrigin = new URL(apiBase).origin;
      const storageOrigin = storageBase ? new URL(storageBase).origin : null;
      if (apiOrigin === storageOrigin) return [];
      return [{ rel: 'preconnect', href: apiOrigin, crossOrigin: 'anonymous' }];
    } catch {
      return [];
    }
  })();

  return (
    <Helmet prioritizeSeoTags>
      <html data-rendered-path={location.pathname} />
      {(pageReady || fallbackReady) && <html data-page-ready="true" />}
      {/* Primary Meta Tags */}
      <title>{merged.title}</title>
      <meta name="title" content={merged.title} />
      <meta name="description" content={merged.description} />
      {merged.keywords && <meta name="keywords" content={merged.keywords} />}
      {merged.author && <meta name="author" content={merged.author} />}
      <meta name="robots" content={merged.robots} />
      <meta name="googlebot" content={merged.robots} />
      <meta name="bingbot" content={merged.robots} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="theme-color" content="#ff3f6c" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogConfig.type} />
      <meta property="og:url" content={ogConfig.url} />
      <meta property="og:title" content={ogConfig.title} />
      <meta property="og:description" content={ogConfig.description} />
      <meta property="og:image" content={ogConfig.image} />
      <meta property="og:image:width" content={String(ogConfig.image_width)} />
      <meta property="og:image:height" content={String(ogConfig.image_height)} />
      <meta property="og:site_name" content={ogConfig.site_name} />
      {ogConfig.image_alt && <meta property="og:image:alt" content={ogConfig.image_alt} />}
      {merged.locale && <meta property="og:locale" content={merged.locale} />}

      {/* Article specific tags */}
      {article && (
        <>
          {article.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.section && <meta property="article:section" content={article.section} />}
          {article.tags && article.tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Product specific tags */}
      {product && (
        <>
          <meta property="product:price:amount" content={String(product.price)} />
          <meta property="product:price:currency" content={product.currency || 'INR'} />
          <meta property="product:availability" content={product.availability || 'in stock'} />
          {product.brand && <meta property="product:brand" content={product.brand} />}
          {product.condition && <meta property="product:condition" content={product.condition} />}
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterConfig.card} />
      {twitterConfig.site && <meta name="twitter:site" content={twitterConfig.site} />}
      {twitterConfig.creator && <meta name="twitter:creator" content={twitterConfig.creator} />}
      <meta name="twitter:title" content={twitterConfig.title} />
      <meta name="twitter:description" content={twitterConfig.description} />
      <meta name="twitter:image" content={twitterConfig.image} />

      {/* Pagination */}
      {prev && <link rel="prev" href={buildCanonicalUrl(prev)} />}
      {next && <link rel="next" href={buildCanonicalUrl(next)} />}

      {/* Performance Hints */}
      {performanceHints.map((hint, idx) => (
        <link
          key={`hint-${idx}`}
          rel={hint.rel}
          href={hint.href}
          {...(hint.crossOrigin && { crossOrigin: hint.crossOrigin })}
        />
      ))}

      {/* Additional Meta Tags */}
      {additionalMeta.map((meta, idx) => (
        <meta key={`meta-${idx}-${meta.name || meta.property}`} {...meta} />
      ))}

      {/* Structured Data (JSON-LD) */}
      {structuredDataEntries.map((schema, idx) => (
        <script key={`jsonld-${idx}`} type="application/ld+json">
          {JSON.stringify(schema, null, 0)}
        </script>
      ))}

      {children}
    </Helmet>
  );
};

export default SEO;
